import React from 'react';
import { X, ArrowLeft, Brain, MessageSquare, ChevronRight, User } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';

interface MemoryHubModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    onOpenAutoSummary: () => void;
    onOpenUserPersona: () => void;
}

const MemoryHubModal: React.FC<MemoryHubModalProps> = ({
    isOpen,
    onClose,
    onBack,
    onOpenAutoSummary,
    onOpenUserPersona
}) => {
    const { isDarkMode } = useTheme();

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        }} onClick={onClose}>
            <div style={{
                width: '500px',
                maxWidth: '90%',
                backgroundColor: isDarkMode ? '#1e293b' : 'white',
                borderRadius: '24px',
                padding: '24px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                color: isDarkMode ? 'white' : '#1e293b'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={onBack}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                borderRadius: '50%',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Memory <Brain size={18} color="#a855f7" />
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            borderRadius: '50%',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Auto-Summary Section */}
                    <div>
                        <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: isDarkMode ? '#e2e8f0' : '#334155' }}>
                            Auto-Summary
                        </h3>

                        <div
                            onClick={onOpenAutoSummary}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '16px',
                                borderRadius: '16px',
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                gap: '16px'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(236, 72, 153, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ec4899',
                                flexShrink: 0
                            }}>
                                <MessageSquare size={20} />
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Analyze conversation</div>
                                <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                    Generate a summary of this chat
                                </div>
                            </div>

                            <ChevronRight size={18} color={isDarkMode ? 'rgba(255,255,255,0.3)' : '#cbd5e1'} />
                        </div>
                    </div>

                    {/* User Persona Section */}
                    <div>
                        <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: isDarkMode ? '#e2e8f0' : '#334155' }}>
                            User Customization
                        </h3>

                        <div
                            onClick={onOpenUserPersona}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '16px',
                                borderRadius: '16px',
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                gap: '16px'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(168, 85, 247, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#a855f7',
                                flexShrink: 0
                            }}>
                                <User size={20} />
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>User Persona</div>
                                <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                    Define who you are in the roleplay
                                </div>
                            </div>

                            <ChevronRight size={18} color={isDarkMode ? 'rgba(255,255,255,0.3)' : '#cbd5e1'} />
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default MemoryHubModal;
